import React, { useState } from 'react';
import { Redirect } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { stashCardTemplate } from '@apps/card/routes/PublicShop/components/login/stashedTemplate';
import { usePublicShopLoginContext } from '@apps/card/routes/PublicShop/PublicShopLoginProvider';
import { LazyEnvelopes } from '@apps/card/routes/Envelopes/Envelopes.lazy';
import { useEnvelopeFallbackPath } from '@apps/card/routes/Envelopes/useEnvelopeFallbackPath';
import type { EnvelopesProps } from '@apps/card/routes/Envelopes/Envelopes';

/**
 * `/cards/envelopes` on the public shop.
 *
 * The same entry component the dashboard mounts, given the one thing only this side can supply: a
 * way to sign in. `PublicShopLoginProvider` — and so the modal — is mounted by `PublicShop`, which is why the
 * handler is built here rather than inside the shared component.
 *
 * The hand-off mirrors `usePublicShopCustomizeNowClickHandler` exactly: stash the request, open
 * the modal, and let `useCreateDraftAndRedirectAfterEventCreation` finish the draft once an event
 * exists. Nothing envelope-specific lives in that tail — the stash carries a
 * `CustomizeTemplateData` whose `format: 'envelope'` the shared creation path already understands.
 */
export const PublicEnvelopes = () => {
  const { goToLogin, isLoginModalOpen } = usePublicShopLoginContext();
  const fallbackPath = useEnvelopeFallbackPath();
  const [hasOfferedSignIn, setHasOfferedSignIn] = useState(false);

  const onUnauthenticated = useEventCallback<NonNullable<EnvelopesProps['onUnauthenticated']>>(data => {
    stashCardTemplate(data);
    setHasOfferedSignIn(true);
    goToLogin();
  });

  // The entry route is a loader waiting on a redirect, so a visitor who closes the sign-in modal
  // would be left staring at it. Closing is an unambiguous signal: `onCloseLoginModal` runs only
  // from the dialog's own close affordance, and the success path deliberately leaves the modal open
  // on `WAITING_FOR_DRAFT` until the draft navigates — so this cannot fire mid-signup.
  if (hasOfferedSignIn && !isLoginModalOpen) {
    return <Redirect to={fallbackPath} />;
  }

  // `LazyEnvelopes`, not `Envelopes`: `PublicShop` imports this wrapper eagerly, so a direct
  // import would pull the whole flag-gated route into every `/cards` initial bundle — defeating
  // the colocated lazy entrypoint the dashboard already uses.
  //
  // No `eventId`: the public shop has no event context, matching how `Gallery` mounts the shared
  // shop router. The guard providers tolerate it, and a visitor who signs up gets one.
  return <LazyEnvelopes onUnauthenticated={onUnauthenticated} />;
};
